'use client';

import { envInfoGet } from '@cecm-microservices-sdk/core/services/utility/env-info';
import { fetchAccountDetails, type AccountDetailsResponse } from '@/services/microservices/account';
import { fetchNextBestActions } from '@/services/microservices/nextBestAction';
import { ensureIdentityGrantSession } from '@/services/microservices/authenticate';
import { getCareBaseUrl } from '@/services/runtimeConfig';
import { buildCareRouteUrlFromBaseUrl } from '@/services/careUrls';
import { updateUseCase } from '@/services/microservices/useCase';
import { getMicroservicesCountryCode } from '@/services/microservices/usedCarEligibility';
import type { InAppPurchasePopoverVariant } from '@/src/ui-components/organisms/Snapshot/InAppPurchasePopover/InAppPurchasePopover';
import type { PartnerSubscriptionVariant } from '@/src/ui-components/organisms/Snapshot/PartnerSubscription/PartnerSubscription';
import type { SnapshotAlertBannerProps } from './SnapshotAlertBanner';
import type { SignInMethod, SignInMethodOption, SnapshotUserErrorType } from './SnapshotUser.types';
import { isSmsAccountUser } from './smsLegacyLogin';
import { getMicroservicesBaseUrl } from './clientSdk';

export interface SnapshotAuthenticatedUser {
    accountNumber: string;
    accountDetailsResponse?: AccountDetailsResponse;
    firstName: string;
    userName: string;
}

export type ClientSdk = NonNullable<BrowserWindow['clientSdk']>;
type ClientSubscriptionsResponse = Awaited<ReturnType<ClientSdk['commerce']['subscription']['getSubscriptions']>>;
type ClientSubscriptionItem = NonNullable<ClientSubscriptionsResponse['items']>[number];
type ClientDetailedStateResponse = Awaited<
    ReturnType<ClientSdk['partnerIntegration']['getDetailedStateBySubscriptionId']>
>;
type ClientOfferDetailsResponse = Awaited<ReturnType<ClientSdk['commerce']['eligibility']['getOfferDetails']>>;
type SubscriptionSourceType =
    | 'stripe'
    | 'google'
    | 'apple'
    | 'amazon'
    | 'roku'
    | 'sms'
    | 'charon'
    | 'internal'
    | 'automotive'
    | 'bango'
    | 'commFleet';

export interface SignupStatusResponse {
    identityId: string;
    hasPassword: boolean;
    hasPasskey: boolean;
    country?: string;
    contactOptions: Array<{
        type: 'EMAIL' | 'PHONE';
        maskedValue: string;
    }>;
    hasUsedContentSampling: boolean;
    items?: Array<{
        country?: string;
    }>;
}

interface DefaultBillingProfileResponse {
    billingProfileName?: {
        firstAndLastName?: {
            firstName?: string;
            lastName?: string;
        };
    };
    country?: string;
    countryCode?: string;
}

interface AccountResponse {
    accountNumber?: string | null;
}

interface IdentityResponse {
    contact?: {
        emailAddress?: string | null;
        phoneNumber?: string | null;
    };
    handle?: {
        emailAddress?: string | null;
    };
    identityId?: string;
    state?: string;
}

type SubscriptionDetailedState = ClientDetailedStateResponse;

type SubscriptionItemResponse = ClientSubscriptionItem & {
    detailedState?: SubscriptionDetailedState;
};

interface SubscriptionResponse {
    items?: SubscriptionItemResponse[];
}

export interface ContactOptionResponse {
    type: 'EMAIL' | 'PHONE';
    maskedValue: string;
}

export interface SubscriptionModalVariant {
    inAppPurchaseVariant?: InAppPurchasePopoverVariant;
    partnerSubscriptionVariant?: PartnerSubscriptionVariant;
    subscribeHref?: string;
}

export interface TeslaManageSelectionData {
    streamingHref: string;
    streamingSubtitle: string;
    streamingTitle: string;
    vehicleHref: string;
    vehicleSubtitle: string;
    vehicleTitle: string;
}

interface PostLoginContext {
    accountResponse?: AccountResponse;
    accountDetailsResponse?: AccountDetailsResponse;
    billingProfileResponse?: DefaultBillingProfileResponse;
    identityResponse?: IdentityResponse;
    subscriptionCountry?: string;
    subscriptionModalVariant?: SubscriptionModalVariant;
    subscriptionsResponse?: SubscriptionResponse;
    teslaManageSelection?: TeslaManageSelectionData;
}

export type PostLoginOutcome =
    | {
          kind: 'automotiveTrial';
      }
    | {
          kind: 'countryMismatch';
          accountCountry: string;
      }
    | {
          kind: 'streamingIdentity';
      }
    | {
          kind: 'nonStandardIdentity';
          identityState: string;
      }
    | {
          kind: 'incompleteTransaction';
      }
    | {
          kind: 'partnerManaged';
          modalVariant: SubscriptionModalVariant;
      }
    | {
          kind: 'teslaManageSelection';
          selection: TeslaManageSelectionData;
      }
    | {
          kind: 'success';
          authenticatedUser: SnapshotAuthenticatedUser;
      }
    | {
          kind: 'smsLegacyLoginRequired';
      };

export const CANADA_ACCOUNT_SIGN_IN_URL = 'http://siriusxm.ca/myaccount';
export const PLANS_PATH = '/plans';
export const PLAYER_LOGIN_URL = 'https://www.siriusxm.com/player/login';

export const buildMethodOptions = (statusResponse: SignupStatusResponse): SignInMethodOption[] => {
    const methodOptions: SignInMethodOption[] = [];
    const phoneContactOption = statusResponse.contactOptions?.find((contactOption) => contactOption.type === 'PHONE');
    const emailContactOption = statusResponse.contactOptions?.find((contactOption) => contactOption.type === 'EMAIL');

    if (phoneContactOption) {
        methodOptions.push({
            value: 'text',
            label: 'Text me a verification code',
            description: `Send to ${phoneContactOption.maskedValue}`,
        });
    }

    if (emailContactOption) {
        methodOptions.push({
            value: 'email',
            label: 'Email me a verification code',
            description: `Send to ${emailContactOption.maskedValue}`,
        });
    }

    if (statusResponse.hasPassword) {
        methodOptions.push({
            value: 'password',
            label: 'Sign in with my password',
        });
    }

    return methodOptions;
};

const getErrorStatus = (error: unknown) =>
    typeof error === 'object' && error
        ? 'status' in error && typeof error.status === 'number'
            ? error.status
            : 'response' in error &&
                error.response &&
                typeof error.response === 'object' &&
                'status' in error.response &&
                typeof error.response.status === 'number'
              ? error.response.status
              : undefined
        : undefined;

const getErrorCode = (error: unknown) => {
    if (typeof error !== 'object' || !error) {
        return '';
    }

    if ('code' in error && typeof error.code === 'string') {
        return error.code.toLowerCase();
    }

    if (
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'data' in error.response &&
        error.response.data &&
        typeof error.response.data === 'object' &&
        'code' in error.response.data &&
        typeof error.response.data.code === 'string'
    ) {
        return error.response.data.code.toLowerCase();
    }

    return '';
};

const isRateLimitedError = (error: unknown) => {
    const status = getErrorStatus(error);
    const code = getErrorCode(error);

    return status === 429 || code.includes('rate_limit') || code.includes('rate-limit') || code.includes('limit');
};

export const mapIdentifierLookupError = (error: unknown): SnapshotUserErrorType => {
    const status = getErrorStatus(error);
    const code = getErrorCode(error);

    if (
        status === 404 ||
        code.includes('invalid_handle') ||
        code.includes('not_found') ||
        code.includes('no_account')
    ) {
        return 'notFound';
    }

    return 'sdkUnavailable';
};

export const mapPasswordError = (error: unknown): SnapshotUserErrorType => {
    const status = getErrorStatus(error);
    const code = getErrorCode(error);

    if (status === 401 || code.includes('invalidcredentials') || code.includes('invalid_credentials')) {
        return 'invalidCredentials';
    }

    return 'sdkUnavailable';
};

export const mapOtpSendError = (error: unknown): SnapshotUserErrorType =>
    isRateLimitedError(error) ? 'otpSendRateLimited' : 'otpSendFailed';

export const mapVerificationCodeError = (error: unknown): SnapshotUserErrorType => {
    const code = getErrorCode(error);

    if (isRateLimitedError(error)) {
        return 'verificationCodeRateLimited';
    }

    if (code.includes('expired')) {
        return 'verificationCodeExpired';
    }

    return 'verificationCodeInvalid';
};

export const getMethodNotificationFromError = (
    errorType: SnapshotUserErrorType,
): SnapshotAlertBannerProps | undefined => {
    if (errorType === 'otpSendRateLimited') {
        return {
            message:
                "You've reached your verification code limit for now. Try again later, or use your password to sign in.",
            tone: 'warning',
        };
    }

    if (errorType === 'otpSendFailed') {
        return {
            message: 'We could not send a verification code right now. Please try another sign-in method.',
            tone: 'warning',
        };
    }

    return undefined;
};

export const getVerificationNotificationFromError = (
    errorType: SnapshotUserErrorType,
): SnapshotAlertBannerProps | undefined => {
    if (errorType === 'verificationCodeRateLimited' || errorType === 'otpSendRateLimited') {
        return {
            message:
                "You've reached your verification code limit for now. You can try again later, or use your passkey or password to sign in.",
            tone: 'warning',
        };
    }

    return undefined;
};

export const runEnvInfoPreflight = async () => {
    const baseUrl = await getMicroservicesBaseUrl();

    if (!baseUrl) {
        return;
    }

    await envInfoGet({
        baseUrl,
    });
};

const getExpiredSubscriptionPurchaseUrl = (careBaseUrl: string) =>
    buildCareRouteUrlFromBaseUrl(careBaseUrl, 'streamingPurchase');

const getCareAccountManageUrl = (careBaseUrl: string) => buildCareRouteUrlFromBaseUrl(careBaseUrl, 'accountManage');

const hasSubscriptionSourceType = (
    source: ClientSubscriptionItem['source'] | undefined,
    sourceType: SubscriptionSourceType,
) => Boolean(source && sourceType in source);

const isExpiredSubscriptionState = (state?: string | null) => {
    const normalizedState = state?.trim().toLowerCase();

    return normalizedState === 'expired' || normalizedState === 'cancelled';
};

const isExpiredSubscriptionVariant = (subscriptionModalVariant?: SubscriptionModalVariant) =>
    subscriptionModalVariant?.inAppPurchaseVariant === 'expired-subscription';

const isThirdPartyManagedSubscriptionVariant = (subscriptionModalVariant?: SubscriptionModalVariant) =>
    Boolean(
        subscriptionModalVariant &&
            (subscriptionModalVariant.partnerSubscriptionVariant ||
                subscriptionModalVariant.inAppPurchaseVariant === 'apple-managed' ||
                subscriptionModalVariant.inAppPurchaseVariant === 'google-managed' ||
                subscriptionModalVariant.inAppPurchaseVariant === 'amazon-managed' ||
                subscriptionModalVariant.inAppPurchaseVariant === 'third-party-managed'),
    );

const getSubscriptionModalVariant = (
    subscriptionsResponse?: SubscriptionResponse,
    careBaseUrl = '',
): SubscriptionModalVariant | undefined => {
    for (const item of subscriptionsResponse?.items ?? []) {
        if (isExpiredSubscriptionState(item.state)) {
            return {
                inAppPurchaseVariant: 'expired-subscription',
                subscribeHref: getExpiredSubscriptionPurchaseUrl(careBaseUrl),
            };
        }

        if (hasSubscriptionSourceType(item.source, 'roku')) {
            return {
                partnerSubscriptionVariant: 'roku',
            };
        }

        if (hasSubscriptionSourceType(item.source, 'apple')) {
            return {
                inAppPurchaseVariant: 'apple-managed',
            };
        }

        if (hasSubscriptionSourceType(item.source, 'google')) {
            return {
                inAppPurchaseVariant: 'google-managed',
            };
        }

        if (hasSubscriptionSourceType(item.source, 'amazon')) {
            return {
                inAppPurchaseVariant: 'amazon-managed',
            };
        }

        if (
            item.source &&
            Object.keys(item.source).length > 0 &&
            !hasSubscriptionSourceType(item.source, 'stripe') &&
            !hasSubscriptionSourceType(item.source, 'sms') &&
            !hasSubscriptionSourceType(item.source, 'automotive')
        ) {
            return {
                inAppPurchaseVariant: 'third-party-managed',
            };
        }
    }

    return undefined;
};

const hasBillingDetails = (billingProfileResponse?: DefaultBillingProfileResponse) => {
    const firstName = billingProfileResponse?.billingProfileName?.firstAndLastName?.firstName?.trim();
    const lastName = billingProfileResponse?.billingProfileName?.firstAndLastName?.lastName?.trim();

    return Boolean(firstName || lastName);
};

const getSubscriptionCountry = (subscriptionsResponse?: SubscriptionResponse) =>
    subscriptionsResponse?.items?.find((item) => item.country)?.country;

const getNonSmsSubscriptions = (subscriptionsResponse?: SubscriptionResponse) =>
    (subscriptionsResponse?.items ?? []).filter((item) => !hasSubscriptionSourceType(item.source, 'sms'));

const getBillingProfileCountry = (billingProfileResponse?: DefaultBillingProfileResponse) =>
    billingProfileResponse?.countryCode ?? billingProfileResponse?.country;

const normalizeCountryCode = (country?: string) => country?.trim().toUpperCase();

const hasEmptySubscriptions = (subscriptionsResponse?: SubscriptionResponse) =>
    Array.isArray(subscriptionsResponse?.items) && subscriptionsResponse.items.length === 0;

const hasIncompleteStripeTransaction = (
    subscriptionsResponse?: SubscriptionResponse,
    billingProfileResponse?: DefaultBillingProfileResponse,
) =>
    !billingProfileResponse &&
    Boolean(
        subscriptionsResponse?.items?.some(
            (item) => hasSubscriptionSourceType(item.source, 'stripe') && item.billingProfileId == null,
        ),
    );

const hasDeviceBeneficiary = (subscription: SubscriptionItemResponse) =>
    Boolean(subscription.beneficiaries?.some((beneficiary) => beneficiary.type === 'device'));

const getProductNames = (offerDetailsResponse?: ClientOfferDetailsResponse) =>
    (offerDetailsResponse?.offer.products ?? []).map((product) => product.name?.trim()).filter(Boolean) as string[];

const prefixSiriusXm = (value: string) => (value.startsWith('SiriusXM ') ? value : `SiriusXM ${value}`);

const getTeslaVehicleTitle = (subscription: SubscriptionItemResponse) => {
    const vehicleState = subscription.detailedState?.vehicleState;
    const year = vehicleState?.year;
    const make = vehicleState?.make?.trim();
    const model = vehicleState?.model?.trim();

    return [year, make, model].filter(Boolean).join(' ');
};

const isTeslaVehicleState = (subscription: SubscriptionItemResponse) =>
    subscription.detailedState?.vehicleState?.make?.trim().toLowerCase() === 'tesla';

const isStripeTeslaSelfPaySubscription = (
    identityResponse: IdentityResponse | undefined,
    subscription: SubscriptionItemResponse | undefined,
) => {
    const lifecycleState = subscription?.detailedState?.lifecycleState;

    if (
        !subscription ||
        identityResponse?.state !== 'ACCOUNT' ||
        !hasSubscriptionSourceType(subscription.source, 'stripe')
    ) {
        return false;
    }

    return Boolean(lifecycleState && 'selfPay' in lifecycleState && isTeslaVehicleState(subscription));
};

const buildTeslaManageSelectionData = (
    subscription: SubscriptionItemResponse,
    offerDetailsResponse: ClientOfferDetailsResponse,
    careBaseUrl: string,
): TeslaManageSelectionData | undefined => {
    const vehicleTitle = getTeslaVehicleTitle(subscription);

    if (!vehicleTitle) {
        return undefined;
    }

    const productNames = getProductNames(offerDetailsResponse);
    const primaryProductName = productNames[0] || '';
    const secondaryProductName = productNames[1] || '';
    const vehicleSubtitle = primaryProductName ? prefixSiriusXm(primaryProductName) : 'SiriusXM subscription';
    const streamingSubtitle = secondaryProductName || primaryProductName || 'Streaming subscription';
    const manageAccountUrl = getCareAccountManageUrl(careBaseUrl);

    return {
        vehicleTitle,
        vehicleSubtitle,
        vehicleHref: manageAccountUrl,
        streamingTitle: 'Streaming subscription',
        streamingSubtitle,
        streamingHref: manageAccountUrl,
    };
};

const isAutomotiveTrialLifecycleState = (subscription: SubscriptionItemResponse) => {
    const lifecycleState = subscription.detailedState?.lifecycleState;

    if (!lifecycleState) {
        return false;
    }

    return 'trialEligible' in lifecycleState || 'trial' in lifecycleState || 'inactive' in lifecycleState;
};

const enrichSubscriptionsWithDetailedState = async (
    clientSdk: ClientSdk,
    subscriptionsResponse?: SubscriptionResponse,
): Promise<SubscriptionResponse | undefined> => {
    if (!subscriptionsResponse?.items?.length) {
        return subscriptionsResponse;
    }

    const enrichedItems: SubscriptionItemResponse[] = await Promise.all(
        subscriptionsResponse.items.map(async (subscription) => {
            if (
                hasSubscriptionSourceType(subscription.source, 'sms') ||
                !subscription.id ||
                !hasDeviceBeneficiary(subscription)
            ) {
                return subscription;
            }

            try {
                const detailedState = await clientSdk.partnerIntegration.getDetailedStateBySubscriptionId({
                    subscriptionId: subscription.id,
                });

                return {
                    ...subscription,
                    detailedState,
                };
            } catch {
                return subscription;
            }
        }),
    );

    return {
        ...subscriptionsResponse,
        items: enrichedItems,
    };
};

export const getAccountCountry = (statusResponse: SignupStatusResponse) =>
    statusResponse.country ?? statusResponse.items?.find((item) => item.country)?.country;

export const getAuthenticatedIdentityId = async (clientSdk: ClientSdk): Promise<string | undefined> => {
    const userInfo = clientSdk.user.getUserInfo();

    if (userInfo.session?.sessionType !== 'authenticated') {
        return undefined;
    }

    const identityResponse = (await clientSdk.user.identity().catch(() => undefined)) as IdentityResponse | undefined;

    return identityResponse?.identityId;
};

const loadPostLoginContext = async (
    clientSdk: ClientSdk,
    careBaseUrl: string,
    options: {
        skipAccountDetails?: boolean;
        skipSmsLegacyDetection?: boolean;
    } = {},
): Promise<PostLoginContext> => {
    const rawSubscriptionsResponse = (await clientSdk.commerce.subscription
        .getSubscriptions()
        .catch(() => undefined)) as ClientSubscriptionsResponse | undefined;
    const subscriptionsResponse = await enrichSubscriptionsWithDetailedState(clientSdk, rawSubscriptionsResponse);
    const subscriptionModalVariant = getSubscriptionModalVariant(subscriptionsResponse, careBaseUrl);

    if (
        isExpiredSubscriptionVariant(subscriptionModalVariant) ||
        isThirdPartyManagedSubscriptionVariant(subscriptionModalVariant)
    ) {
        return {
            subscriptionsResponse,
            subscriptionCountry: normalizeCountryCode(getSubscriptionCountry(subscriptionsResponse)),
            subscriptionModalVariant,
        };
    }

    const accountResponse = (await clientSdk.user.account().catch(() => undefined)) as AccountResponse | undefined;
    const identityResponse = (await clientSdk.user.identity().catch(() => undefined)) as IdentityResponse | undefined;
    const subscriptionCountry = normalizeCountryCode(getSubscriptionCountry(subscriptionsResponse));
    const nonSmsSubscriptions = getNonSmsSubscriptions(subscriptionsResponse);
    const currentSubscription = nonSmsSubscriptions.length === 1 ? nonSmsSubscriptions[0] : undefined;

    if (subscriptionCountry && subscriptionCountry !== 'US') {
        return {
            subscriptionsResponse,
            subscriptionCountry,
            accountResponse,
            identityResponse,
            subscriptionModalVariant,
        };
    }

    if (!options.skipSmsLegacyDetection && isSmsAccountUser({ identityResponse, subscriptionsResponse })) {
        return {
            subscriptionsResponse,
            subscriptionCountry,
            accountResponse,
            identityResponse,
            subscriptionModalVariant,
        };
    }

    if (isStripeTeslaSelfPaySubscription(identityResponse, currentSubscription) && currentSubscription?.offerId) {
        const offerDetailsResponse = (await clientSdk.commerce.eligibility
            .getOfferDetails({
                offerId: currentSubscription.offerId,
            })
            .catch(() => undefined)) as ClientOfferDetailsResponse | undefined;
        const teslaManageSelection = offerDetailsResponse
            ? buildTeslaManageSelectionData(currentSubscription, offerDetailsResponse, careBaseUrl)
            : undefined;

        if (teslaManageSelection) {
            return {
                subscriptionsResponse,
                subscriptionCountry,
                accountResponse,
                identityResponse,
                subscriptionModalVariant,
                teslaManageSelection,
            };
        }
    }

    const countryCode = getMicroservicesCountryCode();
    const accountDetailsResponse =
        !options.skipAccountDetails && accountResponse?.accountNumber
            ? await fetchAccountDetails(countryCode, {
                  accountNumber: accountResponse.accountNumber,
                  identifiedUser: true,
              }).catch(() => undefined)
            : undefined;
    const billingProfileResponse = (await clientSdk.commerce.billing
        .getDefaultBillingProfile()
        .catch(() => undefined)) as DefaultBillingProfileResponse | undefined;

    return {
        subscriptionsResponse,
        subscriptionCountry:
            normalizeCountryCode(getBillingProfileCountry(billingProfileResponse)) ?? subscriptionCountry,
        accountResponse,
        accountDetailsResponse,
        identityResponse,
        billingProfileResponse,
        subscriptionModalVariant,
    };
};

export const getAuthenticatedUserRestoreContext = async (clientSdk: ClientSdk) => {
    const context = await loadPostLoginContext(clientSdk, getCareBaseUrl(), { skipAccountDetails: true });

    return {
        requiresSmsLegacyLogin: isSmsAccountUser(context),
        teslaManageSelection: context.teslaManageSelection,
    };
};

const classifyPostLoginOutcome = (
    {
        accountResponse,
        accountDetailsResponse,
        billingProfileResponse,
        identityResponse,
        subscriptionCountry,
        subscriptionModalVariant,
        subscriptionsResponse,
        teslaManageSelection,
    }: PostLoginContext,
    options: { skipSmsLegacyDetection?: boolean } = {},
): PostLoginOutcome => {
    const nonSmsSubscriptions = getNonSmsSubscriptions(subscriptionsResponse);
    const currentSubscription = nonSmsSubscriptions.length === 1 ? nonSmsSubscriptions[0] : undefined;

    if (
        subscriptionModalVariant &&
        (isExpiredSubscriptionVariant(subscriptionModalVariant) ||
            isThirdPartyManagedSubscriptionVariant(subscriptionModalVariant))
    ) {
        return {
            kind: 'partnerManaged',
            modalVariant: subscriptionModalVariant,
        };
    }

    if (subscriptionCountry && subscriptionCountry !== 'US') {
        return {
            kind: 'countryMismatch',
            accountCountry: subscriptionCountry,
        };
    }

    if (
        hasEmptySubscriptions(subscriptionsResponse) ||
        hasIncompleteStripeTransaction(subscriptionsResponse, billingProfileResponse)
    ) {
        return {
            kind: 'incompleteTransaction',
        };
    }

    if (teslaManageSelection) {
        return {
            kind: 'teslaManageSelection',
            selection: teslaManageSelection,
        };
    }

    if (
        identityResponse?.state === 'ATLAS' &&
        currentSubscription &&
        hasSubscriptionSourceType(currentSubscription?.source, 'automotive') &&
        isAutomotiveTrialLifecycleState(currentSubscription)
    ) {
        return {
            kind: 'automotiveTrial',
        };
    }

    if (identityResponse?.state === 'STREAMING') {
        return {
            kind: 'streamingIdentity',
        };
    }

    if (identityResponse?.state && identityResponse.state !== 'ACCOUNT' && identityResponse.state !== 'ATLAS') {
        return {
            kind: 'nonStandardIdentity',
            identityState: identityResponse.state,
        };
    }

    if (!options.skipSmsLegacyDetection && isSmsAccountUser({ identityResponse, subscriptionsResponse })) {
        return {
            kind: 'smsLegacyLoginRequired',
        };
    }

    if (!hasBillingDetails(billingProfileResponse) && subscriptionModalVariant) {
        return {
            kind: 'partnerManaged',
            modalVariant: subscriptionModalVariant,
        };
    }

    const accountFirstName = accountDetailsResponse?.data.firstName?.trim() || '';
    const billingFirstName = billingProfileResponse?.billingProfileName?.firstAndLastName?.firstName?.trim() || '';
    const billingLastName = billingProfileResponse?.billingProfileName?.firstAndLastName?.lastName?.trim() || '';
    const firstName = accountFirstName || billingFirstName;
    const userName = accountFirstName || [billingFirstName, billingLastName].filter(Boolean).join(' ');

    return {
        kind: 'success',
        authenticatedUser: {
            accountNumber: accountResponse?.accountNumber || '',
            accountDetailsResponse,
            firstName,
            userName,
        },
    };
};

const runSmsLegacyLoginBridge = async (clientSdk: ClientSdk) => {
    const countryCode = getMicroservicesCountryCode();
    await ensureIdentityGrantSession(clientSdk, countryCode);
    await updateUseCase(countryCode, {
        keepCustomerInfo: false,
        identifiedUser: true,
        useCase: 'LOGIN',
    }).catch(() => undefined);
    await fetchNextBestActions(countryCode, {
        locale: 'en-US',
    }).catch(() => undefined);
};

export const completeAuthentication = async (
    clientSdk: ClientSdk,
    careBaseUrl: string,
    options: { hasAttemptedSmsLegacyLogin?: boolean } = {},
): Promise<PostLoginOutcome> => {
    const postLoginContext = await loadPostLoginContext(clientSdk, careBaseUrl, {
        skipSmsLegacyDetection: options.hasAttemptedSmsLegacyLogin,
    });
    const outcome = classifyPostLoginOutcome(postLoginContext, {
        skipSmsLegacyDetection: options.hasAttemptedSmsLegacyLogin,
    });

    if (outcome.kind !== 'smsLegacyLoginRequired') {
        return outcome;
    }

    if (options.hasAttemptedSmsLegacyLogin) {
        throw new Error('SMS login bridge did not resolve the post-login context');
    }

    await runSmsLegacyLoginBridge(clientSdk);

    return completeAuthentication(clientSdk, careBaseUrl, {
        hasAttemptedSmsLegacyLogin: true,
    });
};

export const sendVerificationCode = async ({
    clientSdk,
    nextIdentityId,
    method,
}: {
    clientSdk: ClientSdk;
    nextIdentityId: string;
    method: SignInMethod;
}): Promise<void> => {
    await clientSdk.user.initializeOTP({
        identityId: nextIdentityId,
        language: 'en-US',
        otpContext: 'sign-in',
        otpOption: method === 'text' ? 'PHONE' : 'EMAIL',
    });
};
